import React, { useState } from "react";
import { IntlShape } from "react-intl";
import { toast } from "react-toastify";
import { getTranslation } from "../../../utils/translation/index";
import ConfirmModal from "../../../utils/modal/confirm";
import IntlMessages from "../../../utils/messages";
import { GroundGraphqlContextStore } from "../../../lib/ground-aws-graphql-core";
import FilterSelect from "../Filters/select";
import { EnumFilterFields, EnumFilterType } from "../../../utils/filter";
import { Center } from "../../../lib/ground-aws-graphql-core/models/Center";
import {
  QuoteRequest,
  QuoteRequestStatus,
} from "../../../lib/ground-aws-graphql-core/models/Api/QuoteRequests";
import history from "../../../history";
import { EnumPaths } from "../../../utils/navigation";

interface Props {
  center: Center | null;
  quoteRequestStatus: QuoteRequestStatus[];
  quoteRequest: QuoteRequest;
  intl: IntlShape;
  className: string;
  fromQuoteRequestDetails: boolean;
}

const DropdownQuoteRequestStatus = ({
  quoteRequest,
  quoteRequestStatus,
  intl,
  className,
  fromQuoteRequestDetails,
  center,
}: Props): JSX.Element => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { id, ref, status, quotes } = quoteRequest;

  const currentStatus = {
    value: `${status.code}`,
    label: getTranslation(status.label),
    disabled: status.disabled,
  };

  const [selectedStatus, setSelectedStatus] = useState<{
    value: string;
    label: string;
    disabled: boolean;
  }>(currentStatus);

  const updateQuoteRequest = GroundGraphqlContextStore.useStoreActions(
    actions => actions.quoteRequests.updateQuoteRequest
  );

  const confirmUpdateQuoteRequestStatus = async () => {
    if (selectedStatus) {
      updateQuoteRequest({
        id,
        status_code: parseInt(selectedStatus.value),
      })
        .then(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.requests.change.status.success",
            }),
            { type: "success" }
          );
          setIsModalOpen(false);
        })
        .catch(() => {
          toast(
            intl.formatMessage({
              id: "page.quote.requests.change.status.error",
            }),
            { type: "error" }
          );
          setIsModalOpen(false);
        });
    }
  };

  const field = {
    value: EnumFilterFields[EnumFilterFields.STATUS],
    type: EnumFilterType.SELECT,
    async: false,
    options: quoteRequestStatus.map(o => ({
      label: `${getTranslation(o.label)}`,
      value: `${o.code}`,
      disabled: o.disabled,
    })),
  };

  const showQuoteLink =
    quotes && quotes?.length > 0 && center && fromQuoteRequestDetails;

  return (
    <>
      <div className={`flex flex-col ${className} w-full`} key={ref}>
        <div id={ref} data-cy="quote-request-status-selector" data-testid={ref}>
          <FilterSelect
            selectedValue={currentStatus.value}
            defaultValue={false}
            disabled={currentStatus.disabled}
            className="w-48"
            fields={[field]}
            onChange={(_type, target) => {
              setSelectedStatus(target);
              setIsModalOpen(!isModalOpen);
            }}
          />
        </div>

        {showQuoteLink && (
          <div className="mt-2">
            <button
              className="mb-3 btn-link text-ground-blue-100 text-12px "
              type="button"
              onClick={e => {
                e.preventDefault();
                history.push({
                  pathname: `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${center.id}/${EnumPaths.QUOTES}/${quotes[0].id}/${EnumPaths.EDIT_MODE}`,
                  state: { from: history.location.pathname },
                });
              }}
            >
              <IntlMessages id="page.list.quotes.view.quote" />
            </button>
          </div>
        )}
      </div>
      <ConfirmModal
        isOpen={isModalOpen}
        toggle={() => {
          setIsModalOpen(!isModalOpen);
        }}
        onRequestClose={() => {
          setIsModalOpen(!isModalOpen);
        }}
        handleConfirm={confirmUpdateQuoteRequestStatus}
        content={
          <IntlMessages
            id="page.quote.requests.change.status.confirm"
            values={{
              ref,
              status: selectedStatus?.label,
            }}
          />
        }
      />
    </>
  );
};

export default DropdownQuoteRequestStatus;
